import React, { useState } from 'react';
import { api } from '../lib/api.js';
import { useSession } from '../App.jsx';
import { inr, num } from '../lib/format.js';
import { Alert, Card, CardSkeleton, Empty, StatCard, useAsync } from '../components/ui.jsx';
import { thisMonth } from '../lib/dates.js';
import PeriodPicker from '../components/PeriodPicker.jsx';
import ExportButton, { periodSlug, periodText } from '../components/ExportButton.jsx';

/** GSTR-1 and the outward side of 3B, built from the documents themselves. */
export default function Returns() {
  const session = useSession();
  const entityId = session.entities[0] && session.entities[0].id;
  const [period, setPeriod] = useState(thisMonth);
  const { from, to } = period;
  const [tab, setTab] = useState('gstr1');
  const [downloadError, setDownloadError] = useState(null);

  const r1 = useAsync(() => api.get(`/api/reports/gstr1?entity_id=${entityId}&from=${from}&to=${to}`), [entityId, from, to]);
  // GSTR-1 Phase III splits the HSN summary into a B2B table and a B2C table.
  const hsn = (r1.data && r1.data.hsn) || {};
  const hsnRows = [
    ...(hsn.hsn_b2b || []).map((h) => ({ ...h, section: 'B2B' })),
    ...(hsn.hsn_b2c || []).map((h) => ({ ...h, section: 'B2C' })),
  ];
  const r3 = useAsync(() => api.get(`/api/reports/gstr3b?entity_id=${entityId}&from=${from}&to=${to}`), [entityId, from, to]);

  // A refusal must not be saved as though it were the return.
  const download = async () => {
    setDownloadError(null);
    try {
      const res = await api.raw(`/api/reports/gstr1?entity_id=${entityId}&from=${from}&to=${to}&format=download`);
      const blob = new Blob([JSON.stringify(await res.json(), null, 2)], { type: 'application/json' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `GSTR1-${from}-to-${to}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { setDownloadError(e.message); }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">GST returns</h1>
          <div className="page-sub">
            Built from the invoices and credit notes this application issued, so what is filed and what the customer
            holds cannot drift apart. Inward supplies and input credit stay in Tally.
          </div>
        </div>
        <div className="toolbar" style={{ marginBottom: 0 }}>
          <PeriodPicker value={period} onChange={setPeriod} />
          <ExportButton disabled={!r1.data} build={() => gstWorkbook(r1.data, r3.data, period)} />
        </div>
      </div>

      <div className="segmented" style={{ marginBottom: 16 }} role="group" aria-label="Return">
        {[['gstr1', 'GSTR-1'], ['gstr3b', 'GSTR-3B (outward)']].map(([key, label]) => (
          <button key={key} aria-pressed={tab === key} onClick={() => setTab(key)}>{label}</button>
        ))}
      </div>

      <Alert onRetry={r1.reload}>{r1.error || r3.error}</Alert>
      <Alert onRetry={() => setDownloadError(null)}>{downloadError}</Alert>

      {tab === 'gstr1' && (r1.loading && !r1.data ? <CardSkeleton lines={4} /> : r1.data && (
        <>
          <div className="grid kpi" style={{ marginBottom: 16 }}>
            <StatCard label="Taxable value" value={inr(r1.data.summary.totals.taxable, 0)}
              foot={`${r1.data.summary.invoices} invoices · ${r1.data.summary.credit_notes} credit notes`} />
            <StatCard label="CGST + SGST" value={inr(num(r1.data.summary.totals.cgst) + num(r1.data.summary.totals.sgst), 0)} />
            <StatCard label="IGST" value={inr(r1.data.summary.totals.igst, 0)} />
            <StatCard label="Invoice value" value={inr(r1.data.summary.totals.value, 0)}
              foot={`GSTIN ${r1.data.gstin || '—'} · period ${r1.data.fp}`} />
          </div>

          <Card title="Sections" note="As the offline utility expects them"
            actions={<button className="btn small" onClick={download}>Download JSON</button>}>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Section</th><th>What it holds</th><th className="r">Rows</th><th className="r">Value</th></tr></thead>
                <tbody>
                  <tr><td className="mono">b2b</td><td>Registered buyers</td>
                    <td className="r num">{r1.data.summary.b2b_invoices}</td>
                    <td className="r num">{inr(r1.data.b2b.reduce((a, c) => a + c.inv.reduce((s, i) => s + i.val, 0), 0), 0)}</td></tr>
                  <tr><td className="mono">b2cl</td><td>Unregistered, inter-state, above ₹2.5 lakh</td>
                    <td className="r num">{r1.data.summary.b2cl_invoices}</td>
                    <td className="r num">{inr(r1.data.b2cl.reduce((a, c) => a + c.inv.reduce((s, i) => s + i.val, 0), 0), 0)}</td></tr>
                  <tr><td className="mono">b2cs</td><td>All other unregistered supply, summarised</td>
                    <td className="r num">{r1.data.summary.b2cs_rows}</td>
                    <td className="r num">{inr(r1.data.b2cs.reduce((a, c) => a + num(c.txval), 0), 0)}</td></tr>
                  <tr><td className="mono">cdnr</td><td>Credit notes to registered buyers</td>
                    <td className="r num">{r1.data.cdnr.reduce((a, c) => a + c.nt.length, 0)}</td>
                    <td className="r num">{inr(r1.data.cdnr.reduce((a, c) => a + c.nt.reduce((s, n) => s + n.val, 0), 0), 0)}</td></tr>
                  <tr><td className="mono">hsn</td><td>HSN summary</td>
                    <td className="r num">{hsnRows.length}</td>
                    <td className="r num">{inr(hsnRows.reduce((a, h) => a + num(h.txval), 0), 0)}</td></tr>
                </tbody>
              </table>
            </div>
          </Card>

          <Card title="HSN summary">
            {hsnRows.length === 0 ? <Empty title="Nothing in this period" /> : (
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Table</th><th>HSN</th><th>Description</th><th className="r">Qty</th><th className="r">Rate</th>
                    <th className="r">Taxable</th><th className="r">IGST</th><th className="r">CGST</th><th className="r">SGST</th></tr></thead>
                  <tbody>
                    {hsnRows.map((h) => (
                      <tr key={`${h.section}-${h.hsn_sc}-${h.uqc}-${h.rt}`}>
                        <td>{h.section}</td>
                        <td className="mono">{h.hsn_sc || '—'}</td>
                        <td>{h.desc}</td>
                        <td className="r num">{num(h.qty).toLocaleString('en-IN')} {h.uqc}</td>
                        <td className="r num">{h.rt}%</td>
                        <td className="r num">{inr(h.txval, 0)}</td>
                        <td className="r num">{inr(h.iamt, 0)}</td>
                        <td className="r num">{inr(h.camt, 0)}</td>
                        <td className="r num">{inr(h.samt, 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      ))}

      {tab === 'gstr3b' && (r3.loading && !r3.data ? <CardSkeleton lines={4} /> : r3.data && (
        <>
          <Card title="3.1(a) Outward taxable supplies" note="Net of credit notes issued in the period">
            <div className="table-wrap">
              <table>
                <thead><tr><th>Head</th><th className="r">Taxable value</th><th className="r">IGST</th>
                  <th className="r">CGST</th><th className="r">SGST</th><th className="r">Cess</th></tr></thead>
                <tbody>
                  <tr>
                    <td>Outward taxable supplies (other than zero-rated, nil and exempt)</td>
                    <td className="r num">{inr(r3.data.outward_taxable_supplies.taxable_value, 0)}</td>
                    <td className="r num">{inr(r3.data.outward_taxable_supplies.igst, 0)}</td>
                    <td className="r num">{inr(r3.data.outward_taxable_supplies.cgst, 0)}</td>
                    <td className="r num">{inr(r3.data.outward_taxable_supplies.sgst, 0)}</td>
                    <td className="r num">{inr(r3.data.outward_taxable_supplies.cess, 0)}</td>
                  </tr>
                  {r3.data.outward_zero_rated_supplies && (
                    <tr>
                      <td>3.1(b) Outward taxable supplies (zero-rated)</td>
                      <td className="r num">{inr(r3.data.outward_zero_rated_supplies.taxable_value, 0)}</td>
                      <td className="r num">{inr(r3.data.outward_zero_rated_supplies.igst, 0)}</td>
                      <td className="r num">—</td><td className="r num">—</td>
                      <td className="r num">{inr(r3.data.outward_zero_rated_supplies.cess, 0)}</td>
                    </tr>
                  )}
                  {r3.data.outward_nil_rated_supplies && (
                    <tr>
                      <td>3.1(c) Other outward supplies (nil rated)</td>
                      <td className="r num">{inr(r3.data.outward_nil_rated_supplies.taxable_value, 0)}</td>
                      <td className="r num">—</td><td className="r num">—</td><td className="r num">—</td><td className="r num">—</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {r3.data.outward_reverse_charge_supplies && Math.abs(num(r3.data.outward_reverse_charge_supplies.taxable_value)) > 0 && (
              <div className="card-note" style={{ marginTop: 10 }}>
                Supplies on which the recipient pays tax under reverse charge, kept out of 3.1(a):
                {' '}<b>{inr(r3.data.outward_reverse_charge_supplies.taxable_value, 0)}</b> taxable, with
                {' '}{inr(r3.data.outward_reverse_charge_supplies.tax_payable_by_recipients, 0)} of tax the recipients pay.
                Confirm with your tax adviser which table you report them in.
              </div>
            )}
          </Card>

          <Card title="3.2 Inter-state supplies to unregistered persons" note="Place of supply wise">
            {r3.data.inter_state_supplies_to_unregistered.length === 0
              ? <Empty title="None in this period" />
              : (
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Place of supply</th><th className="r">Taxable value</th><th className="r">IGST</th></tr></thead>
                    <tbody>
                      {r3.data.inter_state_supplies_to_unregistered.map((r) => (
                        <tr key={r.place_of_supply}>
                          <td>{r.place_of_supply}</td>
                          <td className="r num">{inr(r.taxable_value, 0)}</td>
                          <td className="r num">{inr(r.igst, 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            <div className="card-note" style={{ marginTop: 10 }}>{r3.data.note}</div>
          </Card>
        </>
      ))}
    </>
  );
}


// GSTR-1 as rows a reviewer can filter and total: one row per invoice and rate.
const rateRows = (inv, extra) => (inv.itms || []).map(({ itm_det: d }) => ({
  ...extra, inum: inv.inum || inv.nt_num, idt: inv.idt || inv.nt_dt, val: inv.val, pos: inv.pos, rchrg: inv.rchrg,
  inv_typ: inv.inv_typ, against: inv.nt_num ? inv.inum : undefined,
  rt: d.rt, txval: d.txval, iamt: d.iamt || 0, camt: d.camt || 0, samt: d.samt || 0, csamt: d.csamt || 0,
}));
const TAX_COLS = [
  { key: 'rt', header: 'Rate %', type: 'number' },
  { key: 'txval', header: 'Taxable', type: 'money', total: true },
  { key: 'iamt', header: 'IGST', type: 'money', total: true },
  { key: 'camt', header: 'CGST', type: 'money', total: true },
  { key: 'samt', header: 'SGST', type: 'money', total: true },
  { key: 'csamt', header: 'Cess', type: 'money', total: true },
];

function gstWorkbook(r1, r3, period) {
  const sheets = [
    {
      name: 'B2B', title: 'B2B — registered buyers',
      columns: [{ key: 'ctin', header: 'Buyer GSTIN' }, { key: 'inum', header: 'Invoice' }, { key: 'idt', header: 'Date' },
        { key: 'val', header: 'Invoice value', type: 'money' }, { key: 'pos', header: 'POS' },
        { key: 'rchrg', header: 'Reverse charge' }, { key: 'inv_typ', header: 'Type' }, ...TAX_COLS],
      rows: r1.b2b.flatMap((b) => b.inv.flatMap((inv) => rateRows(inv, { ctin: b.ctin }))),
    },
    {
      name: 'B2CL', title: 'B2CL — unregistered, inter-state, large',
      columns: [{ key: 'inum', header: 'Invoice' }, { key: 'idt', header: 'Date' },
        { key: 'val', header: 'Invoice value', type: 'money' }, { key: 'pos', header: 'POS' }, ...TAX_COLS],
      rows: r1.b2cl.flatMap((b) => b.inv.flatMap((inv) => rateRows(inv, {}))),
    },
    {
      name: 'B2CS', title: 'B2CS — other unregistered supply',
      columns: [{ key: 'sply_ty', header: 'Supply' }, { key: 'pos', header: 'POS' }, { key: 'typ', header: 'Type' }, ...TAX_COLS],
      rows: r1.b2cs,
    },
    {
      name: 'CDNR', title: 'CDNR — credit notes to registered buyers',
      columns: [{ key: 'ctin', header: 'Buyer GSTIN' }, { key: 'inum', header: 'Note' }, { key: 'idt', header: 'Date' },
        { key: 'against', header: 'Against invoice' }, { key: 'val', header: 'Note value', type: 'money' },
        { key: 'pos', header: 'POS' }, ...TAX_COLS],
      rows: r1.cdnr.flatMap((b) => b.nt.flatMap((n) => rateRows(n, { ctin: b.ctin }))),
    },
    {
      name: 'HSN', title: 'HSN summary',
      columns: [{ key: 'section', header: 'Table' }, { key: 'hsn_sc', header: 'HSN' }, { key: 'desc', header: 'Description' },
        { key: 'uqc', header: 'UQC' }, { key: 'qty', header: 'Quantity', type: 'number' }, { key: 'rt', header: 'Rate %', type: 'number' },
        { key: 'txval', header: 'Taxable', type: 'money', total: true }, { key: 'iamt', header: 'IGST', type: 'money', total: true },
        { key: 'camt', header: 'CGST', type: 'money', total: true }, { key: 'samt', header: 'SGST', type: 'money', total: true },
        { key: 'csamt', header: 'Cess', type: 'money', total: true }],
      rows: [
        ...((r1.hsn && r1.hsn.hsn_b2b) || []).map((h) => ({ ...h, section: 'B2B' })),
        ...((r1.hsn && r1.hsn.hsn_b2c) || []).map((h) => ({ ...h, section: 'B2C' })),
      ],
    },
  ];
  if (r1.exp && r1.exp.length) {
    sheets.splice(4, 0, {
      name: 'EXP', title: 'Exports',
      columns: [{ key: 'exp_typ', header: 'Type' }, { key: 'inum', header: 'Invoice' }, { key: 'idt', header: 'Date' },
        { key: 'val', header: 'Invoice value', type: 'money' }, { key: 'sbpcode', header: 'Port' },
        { key: 'sbnum', header: 'Shipping bill' }, { key: 'sbdt', header: 'Bill date' }, ...TAX_COLS],
      rows: r1.exp.flatMap((b) => b.inv.flatMap((inv) => (inv.itms || []).map((d) => ({
        exp_typ: b.exp_typ, inum: inv.inum, idt: inv.idt, val: inv.val, sbpcode: inv.sbpcode, sbnum: inv.sbnum, sbdt: inv.sbdt,
        rt: d.rt, txval: d.txval, iamt: d.iamt || 0, csamt: d.csamt || 0,
      })))),
    });
  }
  if (r3) {
    const t = r3.outward_taxable_supplies;
    const z = r3.outward_zero_rated_supplies || {};
    const n = r3.outward_nil_rated_supplies || {};
    const rc = r3.outward_reverse_charge_supplies || {};
    sheets.push({
      name: 'GSTR-3B outward', title: 'GSTR-3B — outward supplies',
      columns: [{ key: 'head', header: 'Head' }, { key: 'taxable', header: 'Taxable value', type: 'money' },
        { key: 'igst', header: 'IGST', type: 'money' }, { key: 'cgst', header: 'CGST', type: 'money' },
        { key: 'sgst', header: 'SGST', type: 'money' }, { key: 'cess', header: 'Cess', type: 'money' }],
      rows: [
        { head: '3.1(a) Outward taxable supplies', taxable: t.taxable_value, igst: t.igst, cgst: t.cgst, sgst: t.sgst, cess: t.cess },
        { head: '3.1(b) Zero-rated supplies', taxable: z.taxable_value, igst: z.igst, cess: z.cess },
        { head: '3.1(c) Nil-rated supplies', taxable: n.taxable_value },
        { head: 'Outward supplies on reverse charge (tax paid by recipients)', taxable: rc.taxable_value },
        ...r3.inter_state_supplies_to_unregistered.map((r) => ({
          head: `3.2 Unregistered, inter-state — POS ${r.place_of_supply}`, taxable: r.taxable_value, igst: r.igst,
        })),
      ],
    });
  }
  return { filename: `gst-returns-${periodSlug(period)}`, title: `GST returns · GSTIN ${r1.gstin || '—'}`, subtitle: periodText(period), sheets };
}
